import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { endOfDay, startOfDay } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MotorDataChart } from "./components/MotorDataChart";
import { MotorDataGrid } from "./components/MotorDataGrid";
import { useMotorStatusData } from "./hooks/useMotorStatusData";
import { buildChartDataset } from "./models/chartDataset";
import { toMotorDataRows } from "./models/MotorDataRow";
import { MotorTheme } from "./models/motorTheme";
import { summariseDiagnostics } from "./models/summariseDiagnostics";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { DateRangeSelect } from "@/components/input/DateRangeSelect";
import { FilterStackLayout } from "@/components/layout/FilterStackLayout";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";
import { DeviceSelect } from "@/pages/live-data/components/DeviceSelect";
import { LiveDataChecklist } from "@/pages/live-data/components/LiveDataChecklist";
import { useGpsOdometerStatusData } from "@/pages/live-data/hooks/useGpsOdometerStatusData";
import {
  LAST_KNOWN_DIAGNOSTIC_IDS,
  useLastKnownStatusData,
} from "@/pages/live-data/hooks/useLastKnownStatusData";
import { useLiveDataLookups } from "@/pages/live-data/hooks/useLiveDataLookups";
import { LiveDataRow } from "@/pages/live-data/models/LiveDataRow";
import { inDisplayUnits, toLiveDataRow } from "@/pages/live-data/models/toLiveDataRow";
import { consumePendingDeviceId } from "@/utilities/navigation/inStructureNavigation";

/** How often readings and status are read again while "Refresh data" is on. */
const LIVE_REFRESH_MS = 30_000;

export default function MotorDataPage() {
  const { t, i18n } = useTranslation();
  const { geotabClient } = useGeotabClient();

  const [selectedDevice, setSelectedDevice] = useState<DeviceWithVehicleInfo | null>(null);
  const [fromDate, setFromDate] = useState(() => startOfDay(new Date()));
  const [toDate, setToDate] = useState(() => endOfDay(new Date()));
  const [chartedIds, setChartedIds] = useState<string[]>([]);
  const [preselectFailed, setPreselectFailed] = useState(false);
  // Off by default: looking into a period does not need it re-read all the time.
  const [liveUpdate, setLiveUpdate] = useState(false);

  const preselectedDeviceId = usePreselectedDeviceId();
  const selectedDeviceId = selectedDevice?.id;
  // Set once the deep link has been applied or the user has chosen for themselves; from then on
  // an empty selection is the user's, not something to fill in again.
  const preselectSettled = useRef(false);

  useEffect(() => {
    // Applied once, and never over a vehicle the user has picked themselves.
    if (!preselectedDeviceId || selectedDevice || preselectSettled.current) {
      return;
    }

    let cancelled = false;
    // Deliberately not through createGroupFilter, unlike the list queries: this is a deep link
    // from another page, and the vehicle the reader came from is the one they meant — narrowing it
    // by whatever the header group filter happens to be set to would answer with nothing.
    geotabClient
      .get("Device", { id: preselectedDeviceId }, 1)
      .then(devices => {
        const found = devices?.[0];
        if (cancelled || preselectSettled.current) {
          return;
        }
        if (found) {
          preselectSettled.current = true;
          setSelectedDevice(current => current ?? found);
        } else {
          setPreselectFailed(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPreselectFailed(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [geotabClient, preselectedDeviceId, selectedDevice]);

  // A period that has already ended cannot gain readings, so there is nothing to keep asking for —
  // and asking anyway re-pages the whole window, which for a long period on a busy vehicle is a
  // great deal of work for an answer that cannot have changed.
  const isCurrentPeriod = toDate.getTime() >= Date.now();
  const liveUpdateActive = liveUpdate && isCurrentPeriod;

  const {
    data: statusData,
    isLoading,
    isFetching,
    error,
  } = useMotorStatusData(
    selectedDeviceId,
    fromDate,
    toDate,
    liveUpdateActive ? LIVE_REFRESH_MS : false,
  );
  const readings = useMemo(() => statusData ?? [], [statusData]);
  // The odometers of a parked vehicle are often missing from the chosen range; the checklist then
  // shows the last figure, marked as old.
  const { data: lastKnownStatusData, isLoading: lastKnownLoading } = useLastKnownStatusData(
    selectedDeviceId,
    LAST_KNOWN_DIAGNOSTIC_IDS,
    fromDate,
  );
  // At the end of the period, or now while it is still running; a running period is left to the
  // hook, so the query key does not move with the clock.
  const { data: gpsOdometerStatusData } = useGpsOdometerStatusData(
    selectedDeviceId,
    toDate.getTime() < Date.now() ? toDate : undefined,
  );

  // Sorted, because the ids are part of the lookups' query key.
  const diagnosticIds = useMemo(
    () =>
      [
        ...new Set(
          [...readings, ...(lastKnownStatusData ?? []), ...(gpsOdometerStatusData ?? [])]
            .map(entry => entry.diagnostic?.id)
            .filter((id): id is string => !!id),
        ),
      ].sort(),
    [readings, lastKnownStatusData, gpsOdometerStatusData],
  );
  // The same names, units and sources the live data page shows for the same reading.
  const {
    lookups,
    isLoading: diagnosticsLoading,
    isError: diagnosticsError,
  } = useLiveDataLookups(diagnosticIds);

  // Converted once into the reader's units, so the table, the checklist and the chart agree.
  const displayReadings = useMemo(() => inDisplayUnits(readings, lookups), [readings, lookups]);
  const summaries = useMemo(
    () => summariseDiagnostics(displayReadings, lookups),
    [displayReadings, lookups],
  );

  const rows = useMemo(
    () =>
      toMotorDataRows(summaries, {
        themeLabel: (theme: MotorTheme) => t(`motorData.theme.${theme}`, theme),
        unknownSource: t("motorData.unknownSource"),
        language: i18n.language,
        missing: "---",
      }),
    [summaries, t, i18n.language],
  );

  const chartedSet = useMemo(() => new Set(chartedIds), [chartedIds]);
  const chartedSummaries = useMemo(
    () => summaries.filter(summary => chartedSet.has(summary.diagnosticId)),
    [summaries, chartedSet],
  );
  const chartDataset = useMemo(
    () => buildChartDataset(displayReadings, chartedIds),
    [displayReadings, chartedIds],
  );

  const onChartedChange = useCallback((diagnosticId: string, charted: boolean) => {
    setChartedIds(current => {
      if (charted) {
        return current.includes(diagnosticId) ? current : [...current, diagnosticId];
      }
      return current.filter(id => id !== diagnosticId);
    });
  }, []);

  // The readings of a vehicle are exactly what the live data checklist reads, so the same
  // checklist can say here whether this vehicle is reporting everything it should.
  const checklistRows = useMemo<LiveDataRow[]>(
    () =>
      summaries.map(summary => ({
        id: summary.diagnosticId,
        diagnosticId: summary.diagnosticId,
        diagnosticName: summary.name,
        value: Number(summary.latest),
        unitOfMeasure: summary.unit,
        dateTime: summary.latestAt,
        source: summary.sourceName ?? summary.sourceId ?? "",
      })),
    [summaries],
  );
  const lastKnownRows = useMemo(
    () => (lastKnownStatusData ?? []).map(entry => toLiveDataRow(entry, lookups)),
    [lastKnownStatusData, lookups],
  );
  const gpsOdometerRows = useMemo(
    () => (gpsOdometerStatusData ?? []).map(entry => toLiveDataRow(entry, lookups)),
    [gpsOdometerStatusData, lookups],
  );

  return (
    <FilterStackLayout
      filters={
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ md: "center" }}
          sx={{ width: "100%" }}
        >
          <Box sx={{ width: "100%" }}>
            {/* The live data page's picker: one vehicle, searchable by name, plate or VIN, and
                each option says whether that vehicle is moving. */}
            <DeviceSelect
              value={selectedDevice}
              onChange={device => {
                preselectSettled.current = true;
                setPreselectFailed(false);
                setSelectedDevice(device);
                setChartedIds([]);
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
              {t("motorData.searchHint")}
            </Typography>
          </Box>
          <DateRangeSelect
            fromDate={fromDate}
            toDate={toDate}
            onChange={(from, to) => {
              // The whole chosen period: the readings are fetched page by page until it is covered.
              if (from) setFromDate(startOfDay(from));
              if (to) setToDate(endOfDay(to));
            }}
          />
          <Tooltip title={isCurrentPeriod ? "" : t("motorData.liveUpdatePastPeriod")}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={liveUpdateActive}
                  disabled={!isCurrentPeriod}
                  onChange={(_event, checked) => setLiveUpdate(checked)}
                />
              }
              label={t("motorData.liveUpdate")}
              sx={{ m: 0, whiteSpace: "nowrap" }}
            />
          </Tooltip>
        </Stack>
      }
      content={
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            p: 2,
            gap: 1,
          }}
        >
          {preselectFailed && !selectedDeviceId && (
            <Alert severity="warning" onClose={() => setPreselectFailed(false)}>
              {t(
                "motorData.preselectFailed",
                "Das verlinkte Fahrzeug konnte nicht geladen werden; bitte ein Fahrzeug auswählen.",
              )}
            </Alert>
          )}
          {error && <Alert severity="error">{error.message}</Alert>}
          {diagnosticsError && (
            <Alert severity="warning">
              {t(
                "motorData.diagnosticsError",
                "Die Namen der Messwerte konnten nicht geladen werden; es werden nur IDs angezeigt.",
              )}
            </Alert>
          )}

          {/* Not while the readings are pending: an empty list would read as a vehicle that
              reports nothing, and the export would say so. */}
          {selectedDeviceId && !isLoading && !diagnosticsLoading && (
            <LiveDataChecklist
              device={selectedDevice as DeviceWithVehicleInfo | null}
              rows={checklistRows}
              lastKnownRows={lastKnownRows}
              lastKnownLoading={lastKnownLoading}
              gpsOdometerRows={gpsOdometerRows}
              periodStart={fromDate}
              isError={!!error || diagnosticsError}
              statusRefreshMs={liveUpdateActive ? LIVE_REFRESH_MS : 0}
            />
          )}

          {!selectedDeviceId ? (
            <Centred>
              <Typography color="text.secondary">{t("motorData.selectAsset")}</Typography>
            </Centred>
          ) : isLoading || diagnosticsLoading ? (
            <Centred>
              <CircularProgress size={28} />
            </Centred>
          ) : error ? null : rows.length === 0 ? (
            <Centred>
              <Typography color="text.secondary">{t("motorData.noData")}</Typography>
            </Centred>
          ) : (
            <>
              <MotorDataGrid
                rows={rows}
                loading={isFetching}
                report={{
                  title: t("motorData.title"),
                  filter: selectedDevice?.name,
                  period: { from: fromDate, to: toDate },
                }}
                exportDisabled={isFetching || !!error}
                chartedIds={chartedSet}
                onChartedChange={onChartedChange}
              />
              {chartedSummaries.length > 0 && (
                <MotorDataChart
                  points={chartDataset.points}
                  series={chartedSummaries}
                  booleanDiagnosticIds={chartDataset.booleanDiagnosticIds}
                />
              )}
            </>
          )}

          {isFetching && rows.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {t("motorData.refreshing")}
            </Typography>
          )}
        </Box>
      }
    />
  );
}

function Centred({ children }: { children: React.ReactNode }) {
  return <Box sx={{ display: "grid", placeItems: "center", width: "100%", py: 6 }}>{children}</Box>;
}

/**
 * The vehicle to start on, from the `deviceId` address bar parameter or handed over by the map.
 *
 * Read once: re-reading it would fight the user the moment they pick something else.
 */
function usePreselectedDeviceId(): string | null {
  const [preselectedDeviceId] = useState<string | null>(() => {
    const fromUrl = new URLSearchParams(window.location.search).get("deviceId")?.trim();
    return fromUrl && fromUrl.length > 0 ? fromUrl : consumePendingDeviceId("motorData");
  });
  return preselectedDeviceId;
}
